import { useCallback } from 'react';
import { toast } from 'sonner';
import { useIsAuthenticated } from './redux';
import { useAppSelector } from './redux';

type UseContactSupportOptions = {
  initialMessage?: string;
};

export type ContactApp = 'whatsapp' | 'telegram' | 'imo';
type ContactScope = 'public' | 'agent';

const contactNames: Record<ContactApp, string> = {
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  imo: 'IMO',
};

export const useContactSupport = (options?: UseContactSupportOptions) => {
  const isAuthenticated = useIsAuthenticated();

  const contactSupport = useAppSelector((state) => state.account.contactSupport.owner);
  const agentContactSupport = useAppSelector((state) => state.account.contactSupport.agent);
  const whatsapp = contactSupport?.whatsapp ?? null;
  const telegram = contactSupport?.telegram ?? null;
  const imo = contactSupport?.imo ?? null;
  const agentWhatsapp = agentContactSupport?.whatsapp ?? null;

  const openContactApp = useCallback(
    (type: ContactApp, scope: ContactScope = 'public') => {
      if (scope === 'agent' && !isAuthenticated) {
        toast.error('Please login to contact our support team.', { id: 'support-auth' });
        return;
      }

      const contacts = scope === 'agent' ? agentContactSupport : contactSupport;
      const contactUrl = contacts?.[type]?.trim();

      if (!contactUrl) {
        toast.error(`${contactNames[type]} support is currently unavailable.`, {
          id: `support-${scope}-${type}`,
        });
        return;
      }

      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

      switch (type) {
        case 'whatsapp': {
          const text = encodeURIComponent(options?.initialMessage ?? 'Hello, I need assistance.');
          const separator = contactUrl.includes('?') ? '&' : '?';
          window.open(`${contactUrl}${separator}text=${text}`, '_blank', 'noopener,noreferrer');
          break;
        }

        case 'telegram': {
          window.open(contactUrl, '_blank', 'noopener,noreferrer');
          break;
        }

        case 'imo': {
          const username = contactUrl.split('/').filter(Boolean).pop();

          if (isMobile && username) {
            window.location.href = `imo://chat?username=${encodeURIComponent(username)}`;

            setTimeout(() => {
              if (document.visibilityState === 'visible') {
                window.open(contactUrl, '_blank', 'noopener,noreferrer');
              }
            }, 1000);
          } else {
            window.open(contactUrl, '_blank', 'noopener,noreferrer');
          }
          break;
        }
      }
    },
    [agentContactSupport, contactSupport, isAuthenticated, options?.initialMessage]
  );

  const openAgentContactApp = useCallback(
    (type: ContactApp) => openContactApp(type, 'agent'),
    [openContactApp]
  );

  const handlePublicWhatsAppSupportClick = useCallback(
    () => openContactApp('whatsapp'),
    [openContactApp]
  );

  const handlePublicTelegramSupportClick = useCallback(
    () => openContactApp('telegram'),
    [openContactApp]
  );

  const handleAgentWhatsAppSupportClick = useCallback(
    () => openAgentContactApp('whatsapp'),
    [openAgentContactApp]
  );

  return {
    whatsapp,
    telegram,
    imo,
    agentWhatsapp,
    openContactApp,
    openAgentContactApp,
    handleAgentWhatsAppSupportClick,
    handlePublicWhatsAppSupportClick,
    handlePublicTelegramSupportClick,
  };
};

export default useContactSupport;
